import { useEffect, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

import DuplicateGroupCard from "../../components/accounts/DuplicateGroupCard";
import useNotification from "../../components/notifications/useNotifications";
import Card from "../../components/shared/Card/Card";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useAccountDuplicates from "../../data/useAccountDuplicates";
import { IDuplicateGroup } from "../../models/account/IDuplicates";
import { elapsed } from "../../utils/contactDuplicates";

const PAGE_SIZE = 25;

// A spinner for the slow jobs on this page, with how long it has been going, so it never looks stuck.
function SlowWork({ message }: { message: string }) {
  const { t } = useTranslation("accounts");
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds((Date.now() - started) / 1000), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <LoadingWheel />
      <p className="text-sm font-medium text-gray-900">{message}</p>
      <p className="text-sm text-gray-500">
        {t("Running for {{time}}. Keep this page open until it finishes.", { time: elapsed(seconds) })}
      </p>
    </div>
  );
}

// Admin clean-up for people who exist more than once - the same eBay buyer or seller arriving as a new
// contact (and account) again and again, or an account that lost its link to its contact. The tidy
// removes only duplicates with nothing attached; the rest are merged here by hand.
//
// Finding them reads every contact, so it runs when asked, not on page load.
export default function AccountDuplicates() {
  const { t } = useTranslation("accounts");
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { duplicates, tidy, mergeAccounts, mergeContacts, dropGroup } = useAccountDuplicates(false, "AccountDuplicates");
  const [onlyReview, setOnlyReview] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [tidyOpen, setTidyOpen] = useState(false);
  const [tidyDone, setTidyDone] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 300);

  useEffect(() => {
    setInfo({
      name: t("Duplicate Accounts"),
      desc: t("Find people who exist more than once and fold them back into one contact and one account"),
    });
  }, [t]);

  const report = duplicates.data?.data;

  const matches = (group: IDuplicateGroup, term: string) => {
    const st = term.toLowerCase();
    return (
      group.name?.toLowerCase().includes(st) ||
      group.matchedOn.some((m) => m.toLowerCase().includes(st)) ||
      group.contacts.some((c) => c.email?.toLowerCase().includes(st) || c.ebayUsername?.toLowerCase().includes(st)) ||
      group.accounts.some((a) => String(a.accountNumber).includes(st) || a.name?.toLowerCase().includes(st))
    );
  };

  const groups = useMemo(
    () =>
      (report?.groups ?? [])
        .filter((g) => !onlyReview || g.needsReview)
        .filter((g) => !debouncedSearch || matches(g, debouncedSearch)),
    [report, onlyReview, debouncedSearch]
  );

  useEffect(() => setShown(PAGE_SIZE), [onlyReview, debouncedSearch]);

  const findDuplicates = () => {
    setTidyDone(false);
    duplicates.refetch();
  };

  const runTidy = () =>
    tidy.mutate(false, {
      onSuccess: (res) => {
        setTidyOpen(false);
        // What is left has changed: the old report would only mislead. Find again to see it.
        duplicates.remove();
        setTidyDone(true);
        addNotification({
          variant: "success",
          primaryText: t("Duplicates tidied"),
          secondaryText: [
            t("Removed {{contacts}} contacts and {{accounts}} accounts; re-linked {{relinked}} contacts.", {
              contacts: res.data.contactsRemoved,
              accounts: res.data.accountsRemoved,
              relinked: res.data.contactsRelinked,
            }),
            res.data.groupsSkipped > 0
              ? t("{{skipped}} left alone because an account is tied to PartPro Trade - merge those by hand.", {
                  skipped: res.data.groupsSkipped,
                })
              : null,
          ]
            .filter(Boolean)
            .join(" "),
        });
      },
      onError: (error: any) => {
        setTidyOpen(false);
        addNotification({ variant: "error", primaryText: t("The tidy did not finish"), secondaryText: error?.message });
      },
    });

  if (duplicates.isFetching) {
    return (
      <div className="mt-8 my-4 bg-white rounded-lg shadow">
        <SlowWork message={t("Checking every contact for people who exist more than once. On a big database this takes a few minutes.")} />
      </div>
    );
  }

  if (tidy.isLoading && !tidyOpen) {
    return (
      <div className="mt-8 my-4 bg-white rounded-lg shadow">
        <SlowWork message={t("Tidying empty duplicates. This rebuilds the list first, so it takes a few minutes too.")} />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="mt-8 space-y-4">
        <Card title={t("Find duplicates")} bodyClassName="p-4 space-y-4">
          {tidyDone && (
            <p className="rounded-md bg-green-50 p-3 text-sm text-green-800">
              {t("Tidy finished. Find duplicates again to see who is left to merge by hand.")}
            </p>
          )}
          {duplicates.isError && (
            <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {t("Finding duplicates did not finish: {{message}}", { message: (duplicates.error as any)?.message ?? "" })}
            </p>
          )}
          <p className="text-sm text-gray-700">
            {t(
              "Wilma checks every contact for the same person saved more than once: the same eBay username, the same email and mobile, or the same name with the same email, phone or address. It reads the whole database, so it can take a few minutes on a big one."
            )}
          </p>
          <div className="flex justify-end">
            <ButtonInput label={t("Find duplicates")} isSubmit={false} onClick={findDuplicates} classes="" />
          </div>
        </Card>
      </div>
    );
  }

  const nothingToTidy = report.tidyableContacts + report.tidyableAccounts + report.relinkedContacts === 0;

  return (
    <div className="mt-8 space-y-4 mb-24">
      <ConfirmModal
        open={tidyOpen}
        setOpen={setTidyOpen}
        isLoading={tidy.isLoading}
        title={t("Tidy empty duplicates?")}
        message={t(
          "This removes {{contacts}} duplicate contacts and {{accounts}} duplicate accounts that have nothing attached, and re-links {{relinked}} contacts to the account that lost them.\n\nAnything with history - a lead, an invoice, an order, a document - is left exactly as it is for you to merge by hand. Removed rows are hidden rather than erased; Wilma support can bring one back if needed.\n\nIt is slow: on a big database it takes a few minutes. Keep this page open until it finishes.",
          {
            contacts: report.tidyableContacts,
            accounts: report.tidyableAccounts,
            relinked: report.relinkedContacts,
          }
        )}
        confirmButtonText={t("Tidy")}
        onConfirm={runTidy}
      >
        {tidy.isLoading && <SlowWork message={t("Tidying empty duplicates.")} />}
      </ConfirmModal>

      <Card title={t("Summary")} bodyClassName="p-4 space-y-4">
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("People found")}</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{report.groups.length}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Empty duplicates")}</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">
              {t("{{contacts}} contacts, {{accounts}} accounts", {
                contacts: report.tidyableContacts,
                accounts: report.tidyableAccounts,
              })}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Contacts to re-link")}</dt>
            <dd className="mt-1 text-2xl font-semibold text-gray-900">{report.relinkedContacts}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t("Need review")}</dt>
            <dd className="mt-1 text-2xl font-semibold text-amber-700">{report.groupsNeedingReview}</dd>
          </div>
        </dl>

        <p className="text-sm text-gray-600">
          {t("The empty duplicates and re-links are exactly what a tidy would do. Found at {{time}}.", {
            time: new Date(duplicates.dataUpdatedAt).toLocaleTimeString(),
          })}
        </p>

        <div className="flex flex-wrap justify-end gap-2">
          <ButtonInput label={t("Find again")} isSubmit={false} onClick={findDuplicates} classes="" />
          <ButtonInput
            label={t("Tidy empty duplicates")}
            isSubmit={false}
            onClick={() => setTidyOpen(true)}
            classes=""
            disabled={nothingToTidy}
          />
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-96 max-w-full pl-10 sm:text-sm"
            placeholder={t("Name, email, eBay username or account no")}
          />
        </div>
        <label className="flex items-center gap-x-1 text-sm font-medium text-gray-500">
          <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} />
          {t("Only people needing review")}
        </label>
      </div>

      {groups.length === 0 ? (
        <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-600 shadow">
          {report.groups.length === 0 ? t("No duplicates found.") : t("Nothing matches your search.")}
        </p>
      ) : (
        <>
          {groups.slice(0, shown).map((group) => (
            // The size is in the key so a group a merge just shrank starts afresh, with no ticks left
            // on rows that have gone.
            <DuplicateGroupCard
              key={`${group.keptContactId}-${group.contacts.length}-${group.accounts.length}`}
              group={group}
              mergeAccounts={mergeAccounts}
              mergeContacts={mergeContacts}
              onMerged={() => dropGroup(group.keptContactId)}
            />
          ))}
          {groups.length > shown && (
            <div className="flex justify-center">
              <ButtonInput
                label={t("Show more ({{remaining}} left)", { remaining: groups.length - shown })}
                isSubmit={false}
                onClick={() => setShown((s) => s + PAGE_SIZE)}
                classes=""
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
